/**
 * Polling utility for repeatedly invoking an async function on a fixed interval
 * until a condition is met, a timeout is reached, the maximum number of attempts
 * is exceeded, or an abort signal is triggered.
 */

export interface PollingOptions<T> {
  /** Fixed interval between polling attempts in milliseconds (required) */
  intervalMs: number;
  /** Maximum amount of time to keep polling in milliseconds */
  timeoutMs?: number;
  /** Maximum number of attempts before giving up (includes the first attempt) */
  maxAttempts?: number;
  /** If true, invoke the function immediately before the first delay (default: true) */
  immediate?: boolean;
  /** Predicate that determines when polling is successful and should stop */
  until: (result: T, attempt: number) => boolean;
  /** Called after each attempt with the result (if successful) */
  onTick?: (result: T | undefined, attempt: number, nextDelayMs: number) => void;
  /** Called when an attempt throws */
  onError?: (error: unknown, attempt: number) => void;
  /** Whether to continue polling after an error (default: true) */
  shouldContinueOnError?: (error: unknown, attempt: number) => boolean;
  /** Optional abort signal to cancel polling */
  signal?: AbortSignal;
}

/**
 * Error thrown when polling terminates without meeting the success condition.
 */
export class PollingError extends Error {
  public readonly attempts: number;
  public readonly lastResult: unknown;
  public readonly lastError: unknown;
  public readonly reason: "timeout" | "maxAttempts" | "aborted";

  constructor(
    attempts: number,
    reason: "timeout" | "maxAttempts" | "aborted",
    lastResult: unknown,
    lastError: unknown,
  ) {
    super(`Polling stopped after ${attempts} attempt(s) due to ${reason}`);
    this.name = "PollingError";
    this.attempts = attempts;
    this.lastResult = lastResult;
    this.lastError = lastError;
    this.reason = reason;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Execute an async function repeatedly on a fixed interval until `until` returns true
 * or termination conditions are met.
 *
 * @example
 * ```ts
 * const result = await poll(
 *   () => fetch('/api/status').then(r => r.json()),
 *   {
 *     intervalMs: 1000,
 *     timeoutMs: 30000,
 *     until: (res) => res.state === 'done',
 *   }
 * );
 * ```
 */
export async function poll<T>(fn: () => Promise<T>, options: PollingOptions<T>): Promise<T> {
  const {
    intervalMs,
    timeoutMs,
    maxAttempts,
    immediate = true,
    until,
    onTick,
    onError,
    shouldContinueOnError = () => true,
    signal,
  } = options;

  if (intervalMs <= 0) {
    throw new Error("poll: intervalMs must be > 0");
  }

  const start = Date.now();
  let attempt = 0;
  let lastResult: T | undefined;
  let lastError: unknown;

  const shouldTimeout = () => typeof timeoutMs === "number" && Date.now() - start >= timeoutMs;
  const exceededAttempts = () => typeof maxAttempts === "number" && attempt >= maxAttempts;

  const checkAbort = () => {
    if (signal?.aborted) {
      throw new PollingError(attempt, "aborted", lastResult, lastError);
    }
  };

  const maybeDelay = async () => {
    if (immediate && attempt === 0) return; // first attempt happens immediately
    await sleep(intervalMs);
  };

  // Loop until success or termination

  while (true) {
    checkAbort();

    if (shouldTimeout()) {
      throw new PollingError(attempt, "timeout", lastResult, lastError);
    }
    if (exceededAttempts()) {
      throw new PollingError(attempt, "maxAttempts", lastResult, lastError);
    }

    await maybeDelay();
    attempt += 1;

    try {
      const result = await fn();
      lastResult = result;

      if (until(result, attempt)) {
        return result;
      }

      onTick?.(result, attempt, intervalMs);
    } catch (error) {
      lastError = error;
      onError?.(error, attempt);

      if (!shouldContinueOnError(error, attempt)) {
        throw error;
      }
    }
  }
}

/**
 * Convenience helpers with common polling configurations
 */
export const pollPresets = {
  /**
   * Fast polling: 500ms interval, 10s timeout
   */
  fast: async <T>(
    fn: () => Promise<T>,
    until: (result: T, attempt: number) => boolean,
    extra?: Omit<PollingOptions<T>, "intervalMs" | "timeoutMs" | "until">,
  ) => poll(fn, { intervalMs: 500, timeoutMs: 10_000, until, ...extra }),

  /**
   * Standard polling: 1s interval, 30s timeout
   */
  standard: async <T>(
    fn: () => Promise<T>,
    until: (result: T, attempt: number) => boolean,
    extra?: Omit<PollingOptions<T>, "intervalMs" | "timeoutMs" | "until">,
  ) => poll(fn, { intervalMs: 1_000, timeoutMs: 30_000, until, ...extra }),

  /**
   * Patient polling: 2s interval, 60s timeout
   */
  patient: async <T>(
    fn: () => Promise<T>,
    until: (result: T, attempt: number) => boolean,
    extra?: Omit<PollingOptions<T>, "intervalMs" | "timeoutMs" | "until">,
  ) => poll(fn, { intervalMs: 2_000, timeoutMs: 60_000, until, ...extra }),
} as const;
